/**
 * Role-aware home — `/` picks the landing page based on the user's
 * strongest role under the 4-role RBAC matrix.
 *
 *   owner / admin   → /admin    (Admin UI surface)
 *   owner / senior  → /reading  (User UI surface, override permitted)
 *   junior          → /reading  (User UI surface, own work only)
 *
 * Owner gets /admin first because it's the more powerful surface;
 * they can navigate to /reading via the nav bar. The rest of the
 * navigation is gated by `can()` in `auth/types.ts`.
 */
import { createMemo, Show } from "solid-js";
import { Navigate } from "@solidjs/router";
import { auth } from "../auth/store";
import type { PacsRole } from "../auth/types";

const LANDING_BY_ROLE: Record<PacsRole, string> = {
  owner:  "/admin",
  admin:  "/admin",
  senior: "/reading",
  junior: "/reading",
};

const ROLE_PRIORITY: readonly PacsRole[] = ["owner", "admin", "senior", "junior"];

export function RoleLanding() {
  const dest = createMemo(() => {
    const a = auth();
    if (!a) return "/login";
    for (const r of ROLE_PRIORITY) {
      if (a.roles.includes(r)) return LANDING_BY_ROLE[r];
    }
    // Logged-in user with no recognised role — surface the problem
    // rather than bouncing silently to /login.
    return "/studies";
  });

  return (
    <Show when={auth()} fallback={<Navigate href="/login" />}>
      <Navigate href={dest()} />
    </Show>
  );
}
